import type { LogMetadata } from '@eluve/logger';

/**
 * Recovery for lazy route chunks the browser can no longer fetch.
 *
 * Splitting the router (see `router.code-splitting.spec.ts`) means most of the
 * app arrives as chunks fetched on demand, so a fetch that fails now takes a
 * route down instead of merely being slow. Without this, such a failure is a
 * dead end: React caches the rejected lazy payload, and the payloads are
 * module-scope constants in `router.tsx`, so the chunk can never succeed again
 * for the life of the tab — the error page's "Try again" re-throws the cached
 * rejection rather than re-fetching.
 *
 * Vite dispatches a cancelable `vite:preloadError` on `window` whenever a
 * dynamic import (or one of its preloaded deps) fails to load, and re-throws
 * unless a listener calls `preventDefault()`. Because every `lazyRoute` call
 * and every local `React.lazy` boundary compiles down to the same
 * `__vitePreload` helper, one listener covers all of them with no per-call-site
 * changes.
 *
 * Reloading is only correct once per URL, though. A chunk that is *permanently*
 * unfetchable — an adblocker filter rule matching a chunk filename, the failure
 * mode that motivated the split — would otherwise reload forever. So an attempt
 * is recorded and suppresses further reloads for the same URL within
 * {@link CHUNK_RELOAD_SUPPRESSION_WINDOW_MS}; the second failure is left to
 * throw so the Sentry error boundary can render.
 *
 * The window is deliberately time-based rather than cleared on a successful
 * boot: the entry bundle always boots fine (the failure happens later, when a
 * route renders), so clearing on boot would re-arm the guard before every
 * failure and reintroduce the reload loop it exists to prevent.
 */

/** `sessionStorage` key holding the last reload attempt. Per-tab by design. */
export const CHUNK_RELOAD_ATTEMPT_STORAGE_KEY = 'eluve:chunk-reload-attempt';

/**
 * How long a recorded attempt suppresses another reload for the same URL. Long
 * enough to outlast a reload plus the failing route's re-render, short enough
 * that a genuine failure days later still gets one recovery attempt.
 */
export const CHUNK_RELOAD_SUPPRESSION_WINDOW_MS = 30_000;

type StoredAttempt = {
  url: string;
  at: number;
};

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type ChunkReloadRecoveryDeps = {
  /** Load-bearing: how often deploys strand tabs is only visible in logs. */
  logger: { warn: (message: string, metadata?: LogMetadata) => void };
  reload?: () => void;
  /** `null` when the browser denies storage (Safari private mode, etc.). */
  storage?: MinimalStorage | null;
  now?: () => number;
  currentUrl?: () => string;
  target?: EventTarget;
};

/**
 * `sessionStorage`, or `null` where it cannot be used.
 *
 * Accessing the property itself throws `SecurityError` in a sandboxed iframe
 * and in some private-browsing modes — `typeof sessionStorage` does *not* guard
 * that, because `typeof` only suppresses `ReferenceError` for undeclared
 * identifiers, not a getter that throws. This resolves during bootstrap, before
 * React mounts, so an escaping throw here would leave a blank page.
 */
const resolveSessionStorage = (): MinimalStorage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

const readLastAttempt = (storage: MinimalStorage): StoredAttempt | null => {
  try {
    const raw = storage.getItem(CHUNK_RELOAD_ATTEMPT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAttempt>;
    return typeof parsed?.url === 'string' && typeof parsed?.at === 'number'
      ? { url: parsed.url, at: parsed.at }
      : null;
  } catch {
    // Unreadable or malformed storage must not break recovery — treat it as
    // "no attempt recorded" and allow the reload.
    return null;
  }
};

const recordAttempt = (storage: MinimalStorage, attempt: StoredAttempt) => {
  try {
    storage.setItem(CHUNK_RELOAD_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // Storage is full or denied. Reloading without a record risks a loop, so
    // the caller checks for this and declines to reload instead.
  }
};

const attemptWasRecorded = (
  storage: MinimalStorage,
  attempt: StoredAttempt,
): boolean => {
  const stored = readLastAttempt(storage);
  return stored?.url === attempt.url && stored?.at === attempt.at;
};

/**
 * Registers the `vite:preloadError` listener. Returns a function that removes
 * it again (used by tests; the app installs it for the page's lifetime).
 */
export const installChunkReloadRecovery = ({
  logger,
  reload = () => window.location.reload(),
  storage = resolveSessionStorage(),
  now = () => Date.now(),
  currentUrl = () => window.location.href,
  target = window,
}: ChunkReloadRecoveryDeps): (() => void) => {
  const handler = (event: Event) => {
    const url = currentUrl();
    const metadata: LogMetadata = {
      url,
      error: (event as VitePreloadErrorEvent).payload?.message,
    };

    // No storage means no loop guard, and an unguarded reload on a permanently
    // missing chunk would spin forever. Let the error surface instead.
    if (!storage) {
      logger.warn(
        'Lazy chunk failed to load; no storage available for a reload guard',
        metadata,
      );
      return;
    }

    const lastAttempt = readLastAttempt(storage);
    const suppressed =
      lastAttempt?.url === url &&
      now() - lastAttempt.at < CHUNK_RELOAD_SUPPRESSION_WINDOW_MS;

    if (suppressed) {
      // Already reloaded for this URL and it failed again — the chunk is
      // genuinely unreachable. Let Vite re-throw into the error boundary.
      logger.warn(
        'Lazy chunk failed to load again after a reload; not retrying',
        metadata,
      );
      return;
    }

    const attempt: StoredAttempt = { url, at: now() };
    recordAttempt(storage, attempt);

    // If the attempt could not be persisted the guard is not in place, so a
    // reload could loop. Surface the error rather than risk that.
    if (!attemptWasRecorded(storage, attempt)) {
      logger.warn(
        'Lazy chunk failed to load; could not persist a reload guard',
        metadata,
      );
      return;
    }

    logger.warn('Lazy chunk failed to load; reloading once', metadata);
    // Suppress Vite's re-throw — this handler owns the failure now.
    event.preventDefault();
    reload();
  };

  target.addEventListener('vite:preloadError', handler);
  return () => target.removeEventListener('vite:preloadError', handler);
};
